// Inspired by https://cloud.google.com/speech-to-text/docs/best-practices and https://codelabs.developers.google.com/codelabs/speaking-with-a-webpage.
// Github repo: (https://github.com/googlecodelabs/speaking-with-a-webpage/blob/master/04-speech/src/main/webapp/javascript/main.js).

const AUDIO_BUFFER_SIZE = 4096;
const IDEAL_SMAPLE_RATE = 16000; // Ideal sample rate (Hz) samples per second (We will probably not get this sample rate).
const SAMPLE_SIZE = 16; // Sample depth / bits (bits per sample).
const MAX_INT = Math.pow(2, 16 - 1) - 1;

// Process audio sample, this will send an ArrayBuffer to the server for transcription.
const processAudio = (sendAudioSamples) => (e) => {
  const floatSamples = e.inputBuffer.getChannelData(0);

  // Convert to Int16Array.
  // Inspired by https://github.com/googlecodelabs/speaking-with-a-webpage/blob/master/04-speech/src/main/webapp/javascript/main.js.
  const intSamples = Int16Array.from(floatSamples.map((n) => n * MAX_INT));

  // Send buffer to socket.
  sendAudioSamples(intSamples.buffer);
};

// Initialize an AudioContext & connect a script processor to it.
const initializeContext = (sendAudioSamples) => {
  const currentAudioContext = new (window.AudioContext ||
    window.webkitAudioContext)();

  const scriptProcessor = currentAudioContext.createScriptProcessor(
    AUDIO_BUFFER_SIZE,
    1,
    1
  );
  scriptProcessor.connect(currentAudioContext.destination);
  scriptProcessor.onaudioprocess = processAudio(sendAudioSamples);
  return [scriptProcessor, currentAudioContext];
};

// Request user access to microphone.
const getAudioSource = async ({ currentAudioContext, scriptProcessor }) => {
  const mediaStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      channelCount: 1,
      sampleRate: {
        ideal: IDEAL_SMAPLE_RATE,
      },
      sampleSize: SAMPLE_SIZE,
    },
    video: false,
  });
  currentAudioContext
    .createMediaStreamSource(mediaStream)
    .connect(scriptProcessor);
};

class SttController {
  constructor() {
    this._audioSource = null;
    this._mediaStream = null;
    this._currentAudioContext = null;
    this._scriptProcessor = null;
    this._initialized = false;
  }

  /**
   * Start recording using the microphone. Will initialize the AudioContext (once) and will ask for microphone access from user.
   * Returns true if successful.
   * Throws DOMException when user blocks access to mic.
   */
  async startRecording({ sendAudioSamples, startRecording }) {
    if (this._initialized) {
      return true;
    }
    this._initialized = true;

    if (!this._currentAudioContext) {
      const [scriptProcessor, currentAudioContext] =
        initializeContext(sendAudioSamples);
      this._scriptProcessor = scriptProcessor;
      this._currentAudioContext = currentAudioContext;
    }

    try {
      await getAudioSource({
        currentAudioContext: this._currentAudioContext,
        scriptProcessor: this._scriptProcessor,
      });
    } catch (e) {
      this._initialized = false;
      throw e;
    }
    startRecording(this._currentAudioContext.sampleRate, SAMPLE_SIZE);
    if (this._currentAudioContext.state === "suspended") {
      this._currentAudioContext.resume();
    }
    return true;
  }

  /**
   * Stop the microphone recording. Will disconnect the microphone from the script processor (In order to
   * release the microphone).
   */
  // TODO(uri): even after stop the red dot in browser remains. investigate.
  stopRecording() {
    if (!this._initialized) {
      return;
    }

    if (this._currentAudioContext.state === "running") {
      this._currentAudioContext.suspend();
    }

    if (this._audioSource) {
      this._audioSource.disconnect(this._scriptProcessor);
      this._audioSource = null;
    }

    if (this._mediaStream) {
      const tracks = this._mediaStream.getTracks();
      tracks[0].stop();
      this._mediaStream = null;
    }

    this._initialized = false;
  }
}

export default SttController;
